import React, { useEffect, useCallback } from 'react';
import {
    App,
    Button,
    Collapse,
    DatePicker,
    Divider,
    Flex,
    Form,
    Input,
    Radio,
    Select,
    Space,
    Typography,
    Upload,
} from 'antd';
import { useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import {
    DeleteOutlined,
    UploadOutlined,
    PlusCircleOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import styled from 'styled-components';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

import IntegrationChatIdsImage from 'static/images/example_chat_ids_image.png';

import { HeaderScenarios } from '../../../../communities/components/HeaderScenarios/HeaderScenarios';
import { CANCEL, SAVE } from '../../../../../shared/contants/actions';
import { BroadcastImageUploader } from './BroadcastImageUploader';
import { downloadChatIdsXls } from '../utils/downloadChatIdsXls';
import { isNotEmptyNumericArray } from '../utils/isNotEmptyNumericArray';
import { TelegramTextEditor } from '../../../../../shared/ui/TelegramTextEditor/TelegramTextEditor';
import { IMassBroadcastsFormForm } from '../service/broadcasts.interface';
import { broadcastSegmentation, sendTimeType } from '../utils/options';
import {
    DATE_TIME_SECONDS,
    formatWithTime,
} from '../../../../../shared/contants/date';

dayjs.extend(utc);
dayjs.extend(timezone);

const URL_REGEX = /^https?:\/\/[\w.-]+\.[a-z]{2,}.*$/i;

export const MassBroadcastsForm = ({
    loading,
    onSave,
    setSegmentationType,
    initialValues,
    imageUrl,
}: IMassBroadcastsFormForm) => {
    const { notification } = App.useApp();

    const navigate = useNavigate();
    const formik = useFormik({
        initialValues: initialValues,
        onSubmit: onSave,
    });

    useEffect(() => {
        if (formik.values.timeSelect === sendTimeType.now) {
            formik.setFieldValue(
                'send_at',
                formatWithTime(dayjs().tz('Europe/Moscow'))
            );
        }
    }, [formik.values.timeSelect]);

    const onAddLink = useCallback(() => {
        const links = Array.isArray(formik.values?.linksArray)
            ? [...formik.values.linksArray]
            : [];
        const newLink = {
            id:
                typeof crypto !== 'undefined' && crypto.randomUUID
                    ? crypto.randomUUID()
                    : new Date().toISOString(),
            linkKey: '',
            linkValue: '',
        };
        formik.setFieldValue('linksArray', [...links, newLink]);
    }, [formik]);

    const onChangeLink = useCallback(
        ({ id, linkKey, linkValue }) => {
            formik.setFieldValue(
                'linksArray',
                formik.values?.linksArray.map(item => {
                    if (item.id === id) {
                        return {
                            ...item,
                            linkKey: linkKey,
                            linkValue: linkValue,
                        };
                    }
                    return item;
                })
            );
        },
        [formik]
    );

    const onDeleteLink = useCallback(
        (linkId: string) => {
            formik.setFieldValue(
                'linksArray',
                formik.values?.linksArray.filter(item => item.id !== linkId)
            );
        },
        [formik]
    );

    const onChangeTitle = useCallback(
        (value: string) => {
            formik.setFieldValue('title', value);
        },
        [formik, notification]
    );

    const handleChangeFile = info => {
        if (info.file.status === 'done') {
            notification.success({
                message: 'Файл списка успешно подготовлен к загрузке',
            });
        } else if (info.file.status === 'error') {
            notification.error({
                message: 'Ошибка загрузки файла',
            });
        }
        formik.setFieldValue(`chat_ids`, info.fileList);
    };

    const disabled =
        loading || !formik.values.segmentation || !formik.values.image;

    return (
        <Flex vertical gap={'middle'}>
            <Flex vertical gap={8}>
                <Typography.Title
                    level={1}
                    style={{ margin: 0, marginBottom: '0' }}
                >
                    Массовые рассылки
                </Typography.Title>
            </Flex>
            <HeaderScenarios
                goBack={() => navigate(-1)}
                inputValue={formik.values?.title || ''}
                onChangeHeaderInput={({ target }) => {
                    onChangeTitle(target.value);
                }}
            />
            <Typography.Title
                ellipsis={{
                    rows: 1,
                }}
                level={3}
                style={{
                    marginTop: 0,
                    marginBottom: '.5rem',
                    fontWeight: 600,
                }}
            >
                Основное
            </Typography.Title>
            <Form.Item
                labelCol={{
                    span: 24,
                }}
                label='Сегментация'
                name='segmentation'
                rules={[
                    {
                        required: true,
                        message: '',
                    },
                ]}
            >
                <Select
                    style={{
                        maxWidth: '320px',
                    }}
                    onChange={value => {
                        formik.setFieldValue('segmentation', value);
                        setSegmentationType(value);
                    }}
                    placeholder='Выберите пользователей для рассылки'
                    defaultValue={
                        !formik?.values?.segmentation
                            ? null
                            : formik?.values?.segmentation
                    }
                    options={[
                        {
                            value: broadcastSegmentation.list,
                            label: 'Свой список',
                        },
                        {
                            value: broadcastSegmentation.visitors,
                            label: 'Посетителям магазина',
                        },
                    ]}
                />
                <br />
                {isNotEmptyNumericArray(formik?.values?.chat_ids) && (
                    <a
                        onClick={() =>
                            downloadChatIdsXls(formik?.values?.chat_ids)
                        }
                    >
                        Скачать текущий список telegramIds.xls
                    </a>
                )}
            </Form.Item>
            {formik?.values?.segmentation === broadcastSegmentation.list && (
                <>
                    <Flex gap={8} vertical>
                        <Typography.Text>Список ID</Typography.Text>
                        <Upload
                            onChange={handleChangeFile}
                            beforeUpload={() => false}
                            maxCount={1}
                        >
                            <Button
                                type='default'
                                disabled={
                                    formik?.values?.segmentation !==
                                    broadcastSegmentation.list
                                }
                            >
                                <UploadOutlined /> Загрузите файл
                            </Button>
                        </Upload>
                    </Flex>

                    <Collapse
                        size='middle'
                        defaultActiveKey={1}
                        items={[
                            {
                                key: '1',
                                label: 'Как должен быть сформирован файл?',
                                children: (
                                    <>
                                        <img
                                            alt={'image'}
                                            src={IntegrationChatIdsImage}
                                        />
                                        <Typography.Text>
                                            Создайте файл формата XLS. Укажите в
                                            первом столбец название chat_id.
                                            Заполните ниже необходимые id для
                                            рассылки.
                                        </Typography.Text>
                                    </>
                                ),
                            },
                        ]}
                    />
                </>
            )}
            <Radio.Group
                onChange={event =>
                    formik.setFieldValue(`timeSelect`, event.target?.value)
                }
                defaultValue='desc'
            >
                <Radio value={sendTimeType.now}>Отправить сейчас</Radio>
                <Radio value={sendTimeType.time}>Запланировать отправку</Radio>
            </Radio.Group>
            <Flex gap={8} vertical>
                <DatePicker
                    clearIcon={false}
                    disabled={formik?.values?.timeSelect !== sendTimeType.time}
                    disabledDate={current => {
                        return current && current <= dayjs().add(-1, 'day');
                    }}
                    placeholder={'Выберите дату и время'}
                    format={DATE_TIME_SECONDS}
                    showTime={true}
                    style={{ width: '14rem', marginTop: '.5rem' }}
                    onChange={value => {
                        formik.setFieldValue(
                            'send_at',
                            formatWithTime(dayjs(value).tz('Europe/Moscow'))
                        );
                    }}
                />
            </Flex>
            <Flex gap={16} style={{ maxWidth: '578px' }} vertical>
                <Divider style={{ margin: 0 }} />
                <Typography.Title
                    ellipsis={{
                        rows: 1,
                    }}
                    level={3}
                    style={{
                        marginTop: 0,
                        marginBottom: '.5rem',
                        fontWeight: 600,
                    }}
                >
                    Контент рассылки
                </Typography.Title>
            </Flex>
            <Flex gap={8} vertical>
                <Typography.Text>Изображение</Typography.Text>
                <BroadcastImageUploader
                    imageUrl={imageUrl}
                    formik={formik}
                    values={formik?.values}
                />
                <Typography.Text type={'secondary'}>
                    Тип файлов: PNG, JPG, JPEG, GIF, вес до 5 МБ. Ширина и
                    высота картинки в сумме не должны превышать 10000 пикселей
                </Typography.Text>
            </Flex>
            <Flex gap={16} style={{ maxWidth: '578px' }} vertical>
                <Flex gap={8} vertical>
                    <Typography.Text>
                        {formik?.values?.image
                            ? 'Напишите заголовок сообщения'
                            : 'Сообщение рассылки'}
                    </Typography.Text>

                    <TelegramTextEditor
                        containerHeight={164}
                        placeholder={'Введите текст'}
                        value={formik?.values?.text || ''}
                        onChange={value => {
                            formik.setFieldValue('text', value);
                        }}
                    />
                </Flex>
            </Flex>

            {formik.values?.linksArray &&
                formik.values?.linksArray.map(linkObj => (
                    <Flex gap={16} key={linkObj.id} align={'flex-end'}>
                        <Flex gap={8} vertical>
                            <Typography.Text>Название кнопки</Typography.Text>
                            <Input
                                value={linkObj.linkKey}
                                style={{ width: '283px' }}
                                placeholder='Укажите название кнопки'
                                showCount
                                maxLength={20}
                                onChange={event => {
                                    onChangeLink({
                                        ...linkObj,
                                        linkKey: event.target.value,
                                    });
                                }}
                            />
                        </Flex>
                        <Flex gap={8} vertical>
                            <Typography.Text>URL кнопки</Typography.Text>
                            <>
                                <Input
                                    value={linkObj.linkValue}
                                    style={{ width: '283px' }}
                                    placeholder='Укажите URL'
                                    onChange={event => {
                                        onChangeLink({
                                            ...linkObj,
                                            linkValue: event.target.value,
                                        });
                                    }}
                                    status={
                                        linkObj.linkValue &&
                                        !URL_REGEX.test(linkObj.linkValue)
                                            ? 'error'
                                            : ''
                                    }
                                />
                                {linkObj.linkValue &&
                                    !URL_REGEX.test(linkObj.linkValue) && (
                                        <Typography.Text type='danger'>
                                            Введите корректный URL, например:
                                        </Typography.Text>
                                    )}
                            </>
                        </Flex>
                        <Button
                            onClick={() => onDeleteLink(linkObj.id)}
                            disabled={loading}
                            danger
                        >
                            <DeleteOutlined />
                        </Button>
                    </Flex>
                ))}
            {formik.values?.linksArray?.length < 10 && (
                <StyledButton
                    disabled={false}
                    type={'text'}
                    onClick={onAddLink}
                >
                    <PlusCircleOutlined
                        color={'#6257D2'}
                        style={{ fontSize: '24px' }}
                    />
                    Добавить кнопку
                </StyledButton>
            )}
            <Space wrap style={{ marginTop: '2rem' }}>
                <Button
                    disabled={disabled}
                    onClick={() => onSave(formik.values)}
                    type={'primary'}
                >
                    {SAVE}
                </Button>
                <Button onClick={() => navigate(-1)}>{CANCEL}</Button>
            </Space>
        </Flex>
    );
};
const StyledButton = styled(Button)`
    width: 192px;
    display: flex;
    padding: 0;
    align-items: center;
    gap: 8px;
    color: #6257d2;
`;
